import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { EXTENSION_KEY, EXTENSION_TYPE, getDefaultTabTitle } from '../nativeTabsConstants.ts';
import type { NativeTabsParameters, TabParameters } from '../nativeTabsTypes.ts';
import { getTabColorForPanelColor, type TabColorKey } from '../utils/tabColorTokens.ts';
import { generateTabId } from '../utils/generateTabId.ts';

type NativeTabsAdf = {
	type: 'multiBodiedExtension';
	attrs: {
		extensionType: typeof EXTENSION_TYPE;
		extensionKey: typeof EXTENSION_KEY;
		parameters: NativeTabsParameters;
	};
	content: Array<{
		type: 'extensionFrame';
		content: ADFEntity[];
	}>;
};

type TransformItem = {
	color?: TabColorKey;
	content: ADFEntity[];
	emoji?: string;
	title: string;
};

const unsupportedNativeTabsContentTypes = new Set(['nestedExpand']);

const emptyParagraph = (): ADFEntity => ({ type: 'paragraph' });

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const isAdfEntity = (value: unknown): value is ADFEntity =>
	isRecord(value) && typeof value.type === 'string';

const getContent = (node: ADFEntity): ADFEntity[] =>
	Array.isArray(node.content) && node.content.every(isAdfEntity) ? node.content : [];

const hasUnsupportedNativeTabsContent = (content: ADFEntity[]): boolean =>
	content.some((node) => unsupportedNativeTabsContentTypes.has(node.type));

const panelToItem = (node: ADFEntity): TransformItem | null => {
	if (node.type !== 'panel' && node.type !== 'panel_c1') {
		return null;
	}

	const attrs = isRecord(node.attrs) ? node.attrs : {};
	const content = getContent(node);
	const emoji =
		typeof attrs.panelIconText === 'string'
			? attrs.panelIconText
			: typeof attrs.panelIcon === 'string'
				? attrs.panelIcon
				: undefined;

	if (hasUnsupportedNativeTabsContent(content)) {
		return null;
	}

	return {
		color: getTabColorForPanelColor(attrs.panelColor),
		content: content.length > 0 ? content : [emptyParagraph()],
		...(emoji ? { emoji } : {}),
		title: getDefaultTabTitle(0),
	};
};

const expandToItem = (node: ADFEntity): TransformItem | null => {
	if (node.type !== 'expand') {
		return null;
	}

	const attrs = isRecord(node.attrs) ? node.attrs : {};
	const content = getContent(node);
	if (hasUnsupportedNativeTabsContent(content)) {
		return null;
	}

	return {
		content,
		title:
			typeof attrs.title === 'string' && attrs.title.trim().length > 0
				? attrs.title.trim()
				: getDefaultTabTitle(0),
	};
};

const layoutSectionToItems = (node: ADFEntity): TransformItem[] | null => {
	if (node.type !== 'layoutSection') {
		return null;
	}

	const columns = getContent(node);
	if (columns.length < 2 || !columns.every((column) => column.type === 'layoutColumn')) {
		return null;
	}

	const items: TransformItem[] = [];
	for (const [index, column] of columns.entries()) {
		const content = getContent(column);
		if (hasUnsupportedNativeTabsContent(content)) {
			return null;
		}

		items.push({
			content: content.length > 0 ? content : [emptyParagraph()],
			title: getDefaultTabTitle(index),
		});
	}
	return items;
};

const createNativeTabsAdf = (
	items: TransformItem[],
	extensionTitle: string,
): NativeTabsAdf | null => {
	if (items.length === 0) {
		return null;
	}

	const tabs: TabParameters[] = items.map((item) => ({
		id: generateTabId(),
		title: item.title,
		...(item.color ? { color: item.color } : {}),
		...(item.emoji ? { emoji: item.emoji } : {}),
	}));

	return {
		type: 'multiBodiedExtension',
		attrs: {
			extensionType: EXTENSION_TYPE,
			extensionKey: EXTENSION_KEY,
			parameters: { extensionTitle, tabs },
		},
		content: items.map((item) => ({
			type: 'extensionFrame',
			content: item.content.length > 0 ? item.content : [emptyParagraph()],
		})),
	};
};

export const transformSelectedNodeToNativeTabsAdf = (
	node: ADFEntity | undefined,
	extensionTitle: string = 'Tabs',
): NativeTabsAdf | null => {
	if (!node) {
		return null;
	}

	const item = panelToItem(node) ?? expandToItem(node);
	if (item) {
		return createNativeTabsAdf([item], extensionTitle);
	}

	const layoutItems = layoutSectionToItems(node);
	return layoutItems ? createNativeTabsAdf(layoutItems, extensionTitle) : null;
};

export const isSelectedNodeSupportedForNativeTabsTransform = (
	node: ADFEntity | undefined,
): boolean => transformSelectedNodeToNativeTabsAdf(node) !== null;
